import React from 'react';
import { TableBody as MuiTableBody, TableRow, TableCell, Box, Typography, Skeleton } from '@mui/material';
import { TableRow as CustomTableRow } from './TableRow';
import { getRowKey } from '../utils/formatters';

// Skeleton rows that mimic the real table structure (barritas grises parpadeantes)
const SkeletonRows = ({ colSpan, rowCount = 8 }) => (
  <MuiTableBody>
    {Array.from({ length: rowCount }).map((_, i) => (
      <TableRow key={i}>
        <TableCell colSpan={colSpan} sx={{ py: 0.8, px: 2, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 0.5 }}>
            <Skeleton variant="rounded" width={32} height={18} sx={{ flexShrink: 0, borderRadius: 1 }} />
            <Skeleton variant="rounded" width={`${22 + (i % 3) * 8}%`} height={18} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={`${14 + (i % 4) * 4}%`} height={18} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={`${18 + (i % 2) * 6}%`} height={18} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={`${10 + (i % 3) * 5}%`} height={18} sx={{ flex: 1, borderRadius: 1 }} />
            <Skeleton variant="rounded" width={72} height={24} sx={{ flexShrink: 0, borderRadius: 2 }} />
          </Box>
        </TableCell>
      </TableRow>
    ))}
  </MuiTableBody>
);

export const TableBody = React.memo(({
  data,
  orderedKeys,
  stickyLeftMeta,
  isLoading,
  colSpan,
  onRowClick,
  onAdminStatusClick,
  hasPartidas,
  expandedRowKey,
  onToggleExpand,
  renderRowActions,
  labels,
  selectable,
  selectedIds,
  onSelectRow,
  focusedIndex,
  onFocusRow,
  compactActions = false,
  emptyStateData,
  actionsColumnWidth,
  colorMode = 'full'
}) => {
  // Al estar cargando datos iniciales (cuando aún no hay filas), mostrar los Skeletons
  if (isLoading && (!data || data.length === 0)) {
    return <SkeletonRows colSpan={colSpan} rowCount={8} />;
  }

  if (!data || data.length === 0) {
    const hasActiveFilters = emptyStateData && (
      emptyStateData.activeFiltersList?.length > 0 || emptyStateData.dateRangeStr
    );

    return (
      <MuiTableBody>
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ borderBottom: 'none' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justify: 'center',
              py: 6,
              px: 2,
              textAlign: 'center',
            }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justify: 'center',
                mb: 2,
                fontSize: '1.5rem',
                color: '#64748b'
              }}>
                🔍
              </Box>

              <Typography variant="h6" fontWeight={700} color="#1e293b" sx={{ mb: 1, fontSize: '1.05rem' }}>
                No se encontraron solicitudes
              </Typography>

              {hasActiveFilters ? (
                <Box sx={{
                  maxWidth: 520,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  p: 2,
                  mt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1
                }}>
                  {emptyStateData.dateRangeStr && (
                    <Typography variant="body2" color="#475569" fontSize="0.82rem">
                      📅 <strong>Rango de fechas:</strong> {emptyStateData.dateRangeStr}
                    </Typography>
                  )}

                  {emptyStateData.activeFiltersList?.length > 0 && (
                    <Typography variant="body2" color="#475569" fontSize="0.82rem">
                      🎯 <strong>Filtros aplicados:</strong> {emptyStateData.activeFiltersList.join(' • ')}
                    </Typography>
                  )}

                  {(() => {
                    const list = emptyStateData.activeFiltersList || [];
                    let sugerenciaStr = 'filtros seleccionados';
                    
                    if (list.length > 0) {
                      const filtrosNombres = [];
                      list.forEach(f => {
                        const text = String(f).toLowerCase();
                        if (text.includes('departamento')) filtrosNombres.push('departamento');
                        if (text.includes('centro')) filtrosNombres.push('centro de costo');
                        if (text.includes('proveedor')) filtrosNombres.push('proveedor');
                        if (text.includes('divisa')) filtrosNombres.push('divisa');
                        if (text.includes('empresa')) filtrosNombres.push('empresa');
                        if (text.includes('comprador') || text.includes('compradora')) filtrosNombres.push('comprador');
                        if (text.includes('búsqueda') || text.includes('busqueda') || text.includes('rapida') || text.includes('rápida')) {
                          filtrosNombres.push('búsqueda rápida');
                        }
                      });

                      const uniques = [...new Set(filtrosNombres)];
                      if (uniques.length === 1) {
                        sugerenciaStr = `este ${uniques[0]}`;
                      } else if (uniques.length === 2) {
                        sugerenciaStr = `este ${uniques[0]}/${uniques[1]}`;
                      } else if (uniques.length > 2) {
                        sugerenciaStr = 'estos criterios';
                      }
                    }

                    return (
                      <Typography variant="caption" color="#64748b" fontSize="0.78rem" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                        💡 Es posible que no existan registros para {sugerenciaStr} dentro de este rango de fechas. Intenta ampliar la fecha de inicio o limpiar los filtros.
                      </Typography>
                    );
                  })()}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                  No hay registros disponibles para mostrar.
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
      </MuiTableBody>
    );
  }

  return (
    <MuiTableBody>
      {data.map((row, index) => {
        const key = String(getRowKey(row));
        return (
          <CustomTableRow
            key={`${key}-${index}`}
            data-row-index={index}
            row={row}
            orderedKeys={orderedKeys}
            stickyLeftMeta={stickyLeftMeta}
            onRowClick={onRowClick}
            onAdminStatusClick={onAdminStatusClick}
            hasPartidas={hasPartidas}
            isExpanded={expandedRowKey === key}
            onToggleExpand={() => onToggleExpand(key)}
            renderRowActions={renderRowActions}
            labels={labels}
            selectable={selectable}
            isSelected={selectable ? selectedIds.has(key) : false}
            onSelectRow={onSelectRow}
            isFocused={focusedIndex === index}
            onFocusRow={() => onFocusRow(index)}
            compactActions={compactActions}
            actionsColumnWidth={actionsColumnWidth}
            colorMode={colorMode}
          />
        );
      })}
    </MuiTableBody>
  );
});
